/**
 * ManageSimpleMatterSheet
 *
 * Sheet for editing non-estate matter details including:
 * - Description
 * - Clients (contact search and assignment)
 * - Matter reference
 * - Acting solicitor
 * - Assisting team members
 * - Archive functionality
 */

'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { Loader2, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { useUpdateMatter } from '@/lib/services/matters';
import { useLawFirmUsers } from '@/lib/hooks/use-law-firm-users';
import { TeamMemberSelector } from '@/components/ui/team-member-selector';
import {
  ContactSearchField,
  type SelectedContact
} from '@/components/ui/contact-search-field';
import { type Matter } from '@/lib/services/matters/types';
import { matterKeys, taskKeys } from '@/lib/query-keys';
import { DemoMatterCheckbox } from '@/components/matters/demo-matter-checkbox';
import { resolveDemoMatterFlag } from '@/lib/utils/demo-matter';

// Form schema for simple matter management
const manageSimpleMatterSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  lawFirmInternalReference: z.string().optional(),
  isDemo: z.boolean()
});

type ManageSimpleMatterFormData = z.infer<typeof manageSimpleMatterSchema>;

interface ManageSimpleMatterSheetProps {
  matterId: number;
  matter: Matter | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Delete the matter (VALE-1927); the host closes this sheet and opens the dialog. */
  onRequestDelete?: () => void;
  onSuccess?: () => void;
}

export function ManageSimpleMatterSheet({
  matterId,
  matter,
  open,
  onOpenChange,
  onRequestDelete,
  onSuccess
}: ManageSimpleMatterSheetProps) {
  const queryClient = useQueryClient();
  const updateMatter = useUpdateMatter();
  const { users: lawFirmUsers, loading: usersLoading } = useLawFirmUsers();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Acting solicitor state (deferred - saved on form submit)
  const [selectedSolicitorId, setSelectedSolicitorId] = useState<
    number | undefined
  >(undefined);
  const [_originalSolicitorId, setOriginalSolicitorId] = useState<
    number | undefined
  >(undefined);

  // Assisting members state (deferred - saved on form submit)
  const [selectedAssistingMembers, setSelectedAssistingMembers] = useState<
    string[]
  >([]);
  const [_originalAssistingMembers, setOriginalAssistingMembers] = useState<
    string[]
  >([]);

  // Client contact selection state
  const [selectedClients, setSelectedClients] = useState<SelectedContact[]>([]);

  const form = useForm<ManageSimpleMatterFormData>({
    resolver: zodResolver(manageSimpleMatterSchema),
    defaultValues: {
      description: '',
      lawFirmInternalReference: '',
      isDemo: false
    }
  });

  // Reset the form when `matter` loads, using React's "store info from
  // previous renders" pattern so the sync happens during render rather than
  // in a synchronous effect. Seed prevMatter to `undefined` so the gate
  // fires on first render even when `useMatter` returns cached data — mirrors
  // the identical fix in edit-matter-dialog.tsx. Gating on `open` transitions
  // (the prior approach) broke once this sheet became conditionally mounted
  // only while open (LF-5692/#1154): it now always mounts with `open` already
  // `true`, so a `prevOpen` initialized from `open` never sees a transition.
  const [prevMatter, setPrevMatter] = useState<Matter | null | undefined>(
    undefined
  );
  if (matter !== prevMatter) {
    setPrevMatter(matter);
    if (matter) {
      form.reset({
        description: matter.metadata?.description || '',
        lawFirmInternalReference: matter.lawFirmInternalReference || '',
        isDemo: matter.isDemo ?? false
      });

      // Initialize selected clients from matter.clients
      const clients: SelectedContact[] =
        matter.clients?.map((c) => ({
          id: c.id,
          firstName: c.firstName,
          lastName: c.lastName,
          organisationName: c.organisationName,
          email: null
        })) || [];
      setSelectedClients(clients);

      // Set acting solicitor
      if (matter.actingSolicitor) {
        setOriginalSolicitorId(matter.actingSolicitor.id);
        setSelectedSolicitorId(matter.actingSolicitor.id);
      } else {
        setOriginalSolicitorId(undefined);
        setSelectedSolicitorId(undefined);
      }

      // Set assisting members (using user ID from role assignments)
      // Backend returns MatterRoleDto with nested user object
      const assistingIds =
        matter.roles
          ?.filter((r) => r.role === 'assisting_member')
          .map((r) => String(r.user.id))
          .filter((id) => id !== '') || [];
      setSelectedAssistingMembers(assistingIds);
      setOriginalAssistingMembers(assistingIds);
    }
  }

  // Just update local state - actual API call happens on form submit
  const handleSolicitorChange = (value: string) => {
    const userId = value ? Number(value) : undefined;
    setSelectedSolicitorId(userId);

    // If selected as solicitor, remove from assisting members
    if (userId && selectedAssistingMembers.includes(String(userId))) {
      setSelectedAssistingMembers((prev) =>
        prev.filter((id) => id !== String(userId))
      );
    }
  };

  const onSubmit = async (data: ManageSimpleMatterFormData) => {
    setIsSubmitting(true);
    try {
      // Build roles array for the update payload
      // This replaces all existing role assignments
      const roles: Array<{
        userId: number;
        role: 'acting_solicitor' | 'assisting_member';
      }> = [];

      // Add acting solicitor if selected
      if (selectedSolicitorId != null) {
        roles.push({
          userId: selectedSolicitorId as number,
          role: 'acting_solicitor'
        });
      }

      // Add assisting members
      for (const userIdStr of selectedAssistingMembers) {
        const userId = parseInt(userIdStr, 10);
        if (!isNaN(userId)) {
          roles.push({
            userId,
            role: 'assisting_member'
          });
        }
      }

      // Build the update payload with clientIds
      // Note: metadata.clientNames is intentionally NOT included - clients are
      // managed via clientIds. We only send description in metadata to avoid
      // accidentally clearing or setting empty clientNames on the backend.
      // All contacts have real IDs (created via API before selection).
      const updatePayload = {
        lawFirmInternalReference: data.lawFirmInternalReference || undefined,
        // Resolved against the reference this same save is writing, so a box
        // ticked before the reference was edited cannot submit from behind a
        // control that has since stopped rendering (VALE-749).
        isDemo: resolveDemoMatterFlag(
          data.lawFirmInternalReference,
          data.isDemo
        ),
        metadata: {
          description: data.description
        },
        clientIds: selectedClients.map((c) => c.id),
        roles
      };

      // Debug logging for development
      if (process.env.NODE_ENV === 'development') {
        console.log('[ManageSimpleMatterSheet] Submitting update:', {
          matterId,
          selectedSolicitorId,
          selectedAssistingMembers,
          selectedClients,
          rolesPayload: roles,
          fullPayload: updatePayload
        });
      }

      // Update matter with all details including roles and clientIds
      await updateMatter.mutateAsync({
        matterId,
        data: updatePayload
      });

      // Invalidate caches - include tasks to update dashboard list cards
      queryClient.invalidateQueries({
        queryKey: matterKeys.overview(matterId)
      });
      queryClient.invalidateQueries({
        queryKey: matterKeys.estateAdmin(matterId, 'overview')
      });
      queryClient.invalidateQueries({ queryKey: matterKeys.all });
      // Invalidate tasks cache to update dashboard list cards (Today, Upcoming, etc.)
      // Matter details like description, client names, and team members are displayed in task cards
      queryClient.invalidateQueries({ queryKey: taskKeys.all });

      toast.success('Matter updated successfully');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error updating matter:', error);
      toast.error('Failed to update matter');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isArchived = matter?.isArchived ?? false;

  const handleArchiveToggle = async () => {
    setIsSubmitting(true);
    try {
      await updateMatter.mutateAsync({
        matterId,
        data: isArchived ? { isArchived: false } : { isArchived: true }
      });

      toast.success(
        isArchived
          ? 'Matter unarchived successfully'
          : 'Matter archived successfully'
      );
      queryClient.invalidateQueries({
        queryKey: matterKeys.overview(matterId)
      });
      queryClient.invalidateQueries({ queryKey: matterKeys.all });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error(
        `Error ${isArchived ? 'unarchiving' : 'archiving'} matter:`,
        error
      );
      toast.error(
        isArchived ? 'Failed to unarchive matter' : 'Failed to archive matter'
      );
    } finally {
      setIsSubmitting(false);
      setShowArchiveDialog(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Manage Matter Details</SheetTitle>
          </SheetHeader>

          <Form {...form}>
            <form
              id="manage-simple-matter-form"
              onSubmit={form.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                // Prevent Enter key from submitting form when in input fields
                if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
                  e.preventDefault();
                }
              }}
              className="mt-6 space-y-6"
            >
              {/* Matter Reference */}
              <FormField
                control={form.control}
                name="lawFirmInternalReference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Matter Reference</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter reference number" />
                    </FormControl>
                    {/*
                      Demo matter — nested inside the reference field, because it
                      is the reference that decides whether it is offered at all,
                      and `field.value` is the only reading of it that survives
                      this form's render-phase `reset()`. It renders no wrapper of
                      its own: an empty `FormItem` would still take a margin from
                      the surrounding `space-y-*` while contributing no height,
                      leaving a double gap on every matter whose reference does
                      not qualify.
                    */}
                    <FormField
                      control={form.control}
                      name="isDemo"
                      render={({ field: demoField }) => (
                        <DemoMatterCheckbox
                          id="manage-simple-matter-is-demo"
                          reference={field.value}
                          checked={demoField.value}
                          onCheckedChange={demoField.onChange}
                        />
                      )}
                    />
                  </FormItem>
                )}
              />

              {/* Matter Details Section */}
              <div className="space-y-4">
                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Description <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter matter description"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Client Selection */}
                <ContactSearchField
                  selectedContacts={selectedClients}
                  onChange={setSelectedClients}
                  label="Clients"
                  placeholder="Search contacts to add as client..."
                  allowCreate={true}
                  matterId={matterId}
                />
              </div>

              <Separator />

              {/* Assignment Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Assignment</h3>

                {/* Acting Solicitor */}
                <FormItem>
                  <FormLabel>
                    Acting Solicitor <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={handleSolicitorChange}
                    value={selectedSolicitorId?.toString() || ''}
                    disabled={usersLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select acting solicitor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {lawFirmUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.firstName} {user.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>

                {/* Assisting Team Members */}
                <TeamMemberSelector
                  users={lawFirmUsers}
                  usersLoading={usersLoading}
                  usersError={null}
                  selectedUserIds={selectedAssistingMembers}
                  onSelectionChange={setSelectedAssistingMembers}
                  excludeUserIds={
                    selectedSolicitorId ? [selectedSolicitorId.toString()] : []
                  }
                  immediateAssignment={false}
                  label="Assisting Team Members"
                  description="Select team members to assist with this matter"
                />
              </div>
            </form>
          </Form>

          <SheetFooter className="mt-6 flex-row justify-between sm:justify-between">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                gap="loose"
                tone={isArchived ? 'default' : 'destructive'}
                onClick={() => setShowArchiveDialog(true)}
                disabled={isSubmitting}
              >
                {isArchived ? (
                  <>
                    <ArchiveRestore className="h-4 w-4" />
                    Unarchive
                  </>
                ) : (
                  <>
                    <Archive className="h-4 w-4" />
                    Archive
                  </>
                )}
              </Button>
              {onRequestDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  gap="loose"
                  tone="destructive"
                  onClick={onRequestDelete}
                  disabled={isSubmitting}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                size="xs"
                density="compact"
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                density="compact"
                gap="standard"
                type="button"
                disabled={isSubmitting}
                onClick={form.handleSubmit(onSubmit)}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Archive/Unarchive Confirmation Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArchived ? 'Unarchive Matter' : 'Archive Matter'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArchived
                ? 'Are you sure you want to unarchive this matter? It will be restored to the active matters list.'
                : 'Are you sure you want to archive this matter? Archived matters can be restored from the archive view.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveToggle}
              disabled={isSubmitting}
              className={
                isArchived
                  ? undefined
                  : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isArchived ? 'Unarchiving...' : 'Archiving...'}
                </>
              ) : isArchived ? (
                'Unarchive Matter'
              ) : (
                'Archive Matter'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
